import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Library, Lock, Mic } from 'lucide-react';
import { useToast } from 'components/ui/use-toast';
import { Button } from 'components/ui/button';
import * as TestApi from 'modules/test/api';
import * as Api from '../../api';
import { fixedWritingTask, isSatKind, scopeLabel, scopeMode } from '../../constants';
import type { AuthorSection, ExamConfig, UpdateExamSectionInput } from '../../types';
import { ExamSectionCard } from './ExamSectionCard';
import { ExamSettingsCard } from './ExamSettingsCard';
import { ExamReadinessPanel, useReadinessText } from './ExamReadinessPanel';
import type { ExamQuestionPayload } from './ExamQuestionDialog';

interface Props {
  testId: string;
  config: ExamConfig;
  sections: AuthorSection[];
  status: string;
  totalMinutes?: number;
}

/**
 * Content step for IELTS-style / Digital SAT practice tests: the blueprint
 * sections with their timers, audio, library sources and questions, plus the
 * exam settings and what still blocks activation.
 */
export function ExamBuilder({ testId, config, sections, status, totalMinutes }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const locked = status === 'active' || status === 'published';
  const sat = config.standard === 'SAT';
  const scope = config.ielts?.scope;
  const partScope = scopeMode(scope) === 'PART';

  const readiness = useQuery({
    queryKey: ['exam', 'readiness', testId],
    queryFn: async () => (await Api.Readiness(testId)).data,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['test', 'single', testId] }),
      queryClient.invalidateQueries({ queryKey: ['exam', 'readiness', testId] }),
    ]);
  };

  const sectionTitle = (id: string) => sections.find((s) => s.id === id)?.title;
  const issueText = useReadinessText(sectionTitle);
  // Blocking issues also show on their section card.
  const issuesBySection = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const i of readiness.data?.blocking ?? []) {
      if (!i.sectionId) continue;
      map.set(i.sectionId, [...(map.get(i.sectionId) ?? []), issueText(i)]);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readiness.data, sections]);

  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const groups: Array<{ key: string; title: string; sections: AuthorSection[] }> = sat
    ? (['SAT_RW', 'SAT_MATH'] as const).map((k) => ({
        key: k,
        title: t(k === 'SAT_RW' ? 'exam.kind.sat_rw' : 'exam.kind.sat_math'),
        sections: ordered.filter((s) => s.kind === k),
      }))
    : [{ key: 'ielts', title: '', sections: ordered }];
  const usesLibrary = sections.some((s) => s.source?.mode === 'RANDOM');
  const canAddSpeaking = !sat && !scope && !locked && !sections.some((s) => s.kind === 'IELTS_SPEAKING');

  const addSpeaking = async () => {
    try {
      await Api.AddSpeakingSection(testId);
      await refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('failed_to_save'));
    }
  };

  const handlers = (section: AuthorSection) => ({
    onUpdate: async (data: UpdateExamSectionInput) => {
      await Api.UpdateExamSection(testId, section.id, data);
      await refresh();
    },
    onAddQuestion: async (payload: ExamQuestionPayload) => {
      await TestApi.AddQuestion(section.id, payload as any);
      await refresh();
    },
    onUpdateQuestion: async (questionId: string, payload: ExamQuestionPayload) => {
      const { type: _type, ...rest } = payload;
      await TestApi.UpdateQuestion(questionId, rest as any);
      await refresh();
    },
    onDeleteQuestion: async (questionId: string) => {
      await TestApi.DeleteQuestion(questionId);
      await refresh();
    },
    onSaveToLibrary: async () => {
      await Api.LibrarySaveFromTest(testId, section.id);
      await queryClient.invalidateQueries({ queryKey: ['section-library'] });
    },
    onRemove:
      section.kind === 'IELTS_SPEAKING' && !scope
        ? async () => {
            await Api.RemoveSpeakingSection(testId, section.id);
            await refresh();
          }
        : undefined,
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {sat ? t('exam.format.sat') : t('exam.format.ielts')}
            {!sat && ` · ${config.ielts?.variant === 'GENERAL' ? t('exam.ielts.general') : t('exam.ielts.academic')}`}
            {scope && ` · ${scopeLabel(t, scope)}`}
          </p>
          <h2 className="font-display text-2xl font-bold text-foreground">{t('exam.builder.title')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('exam.builder.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {totalMinutes ? (
            <span className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm font-medium text-foreground">
              <Clock className="h-4 w-4" aria-hidden /> {t('exam.builder.total_time', { minutes: totalMinutes })}
            </span>
          ) : null}
          {!partScope && (
            <Button asChild variant="outline" size="sm" className="gap-1.5">
              <Link to="/app/section-library">
                <Library className="h-4 w-4" aria-hidden /> {t('exam.library.title')}
              </Link>
            </Button>
          )}
        </div>
      </div>

      {locked && (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" aria-hidden /> {t('exam.builder.locked')}
        </p>
      )}

      <ExamReadinessPanel
        readiness={readiness.data}
        loading={readiness.isLoading}
        sectionTitle={sectionTitle}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-8">
          {groups.map((g) => (
            <div key={g.key} className="space-y-3">
              {g.title && <h3 className="font-display text-base font-bold text-foreground">{g.title}</h3>}
              {g.sections.map((section) => (
                <ExamSectionCard
                  key={section.id}
                  testId={testId}
                  section={section}
                  showSourceEditor={!isSatKind(section.kind) || section.moduleTier === 'BASE'}
                  libraryEnabled={!partScope}
                  fixedWritingTask={fixedWritingTask(scope)}
                  locked={locked}
                  issues={issuesBySection.get(section.id) ?? []}
                  {...handlers(section)}
                />
              ))}
            </div>
          ))}
          {canAddSpeaking && (
            <button
              type="button"
              onClick={addSpeaking}
              className="flex w-full items-center gap-4 rounded-xl border border-dashed border-border bg-card p-5 text-left transition-colors hover:border-foreground/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted text-foreground/80">
                <Mic className="h-5 w-5" aria-hidden />
              </span>
              <span>
                <span className="block font-display text-base font-bold text-foreground">{t('exam.speaking.add_section')}</span>
                <span className="block text-sm text-muted-foreground">{t('exam.speaking.add_section_help')}</span>
              </span>
            </button>
          )}
        </div>
        <div className="lg:sticky lg:top-4 lg:self-start">
          <ExamSettingsCard testId={testId} config={config} locked={locked} usesLibrary={usesLibrary} onSaved={refresh} />
        </div>
      </div>
    </div>
  );
}

export default ExamBuilder;
